import React, { useState, useEffect, useRef } from 'react';
import CountUp from 'react-countup';
import colors from 'shared-components/theme/colors';
import { gilroyBold } from 'shared-components/theme/fonts';
import Media from 'shared-components/theme/medias';
import styled, { keyframes, css } from 'styled-components';

const useIntersectionObserver = (ref) => {
    const [isIntersecting, setIsIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsIntersecting(entry.isIntersecting);
            },
            { threshold: 0.3 }
        );

        if (ref.current) observer.observe(ref.current);
        return () => ref.current && observer.unobserve(ref.current);
    }, []);

    return isIntersecting;
};

const AnimatedProgressBar = ({
    percentage,
    label,
    color = 'linear-gradient(90deg, #28a745 0%, #20c997 100%)',
    height = '45px',
    trackColor = 'transparent',
    duration = 2,
    delay = 0,
    textColor,
}) => {
    const ref = useRef();
    const isVisible = useIntersectionObserver(ref);

    return (
        <ProgressContainer ref={ref} height={height} trackColor={trackColor}>
            <ProgressFill
                percentage={percentage}
                color={color}
                animate={isVisible}
                duration={duration}
                delay={delay}>
                {label && (
                    <ProgressLabel textColor={textColor}>
                        $
                        <CountUp
                            start={0}
                            end={label}
                            decimals={2}
                            enableScrollSpy={true}
                        />
                    </ProgressLabel>
                )}
            </ProgressFill>
        </ProgressContainer>
    );
};
export default AnimatedProgressBar;

const fillAnimation = keyframes`
  from { width: 0%; }
  to { width: var(--target-width); }
`;

const ProgressContainer = styled.div`
    width: 100%;
    background-color: ${(props) => props.trackColor || '#e9ecef'};
    height: ${(props) => props.height || '32px'};
    position: relative;
    overflow: hidden;
    // box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);

    ${Media('laptops')} {
        height: 30px;
    }
`;

const ProgressFill = styled.div`
    height: 100%;
    background: ${(props) => props.color};
    position: relative;
    width: 0%;
    position: relative;
    ${(props) =>
        props.animate &&
        css`
            --target-width: ${props.percentage}%;
            animation: ${fillAnimation} ${props.duration}s ease-out forwards;
            animation-delay: ${props.delay}s;
        `}
`;

const ProgressLabel = styled.div`
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    color: ${(props) => props.textColor || colors.white[100]};
    font-family: ${gilroyBold};
    font-weight: 700;
    font-size: 22px;
    z-index: 2;

    ${Media('large')} {
        font-size: 1.309vw;
    }
    ${Media('laptops')} {
        font-size: 16px;
    }
    ${Media('mobile')} {
        font-size: 12px;
    }
`;
